<template>
	<div class="contractor-lists-content">
			<div v-if="popup.show" class="popup-overlay" @click.self="hidePopup">
				<div class="popup-box" :class="popup.type" role="alertdialog" aria-modal="true" aria-live="assertive">
					<div class="popup-icon">{{ popup.type === 'success' ? '✓' : '!' }}</div>
					<p class="popup-message">
						{{ popup.messageBefore }}<strong v-if="popup.fileName">{{ popup.fileName }}</strong>{{ popup.messageAfter }}
					</p>
					<button type="button" class="popup-button" @click="hidePopup">OK</button>
				</div>
			</div>

			<p v-if="uploadError" class="upload-error" role="alert">{{ uploadError }}</p>
			<p v-if="listError" class="upload-error" role="alert">{{ listError }}</p>

			<div class="table-scroll">
				<table class="contractor-table">
					<thead>
						<tr>
							<th scope="col">Contractor List</th>
							<th scope="col">Area</th>
							<th scope="col">Equipment</th>
							<th scope="col">File</th>
							<th scope="col" class="actions-heading">Actions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="isLoadingLists">
							<td colspan="5" class="empty-state">Loading contractor lists...</td>
						</tr>
						<tr v-else-if="!contractorLists.length && !listError">
							<td colspan="5" class="empty-state">No contractor lists found.</td>
						</tr>
						<tr v-for="list in contractorLists" :key="list.id">
							<td>
								<div class="list-name">{{ list.name }}</div>
							</td>
							<td>{{ list.area }}</td>
							<td>{{ list.equipment }}</td>
							<td>
								<span v-if="list.apiManaged || list.fileContent" class="file-name">{{ list.fileName }}</span>
								<span v-else class="empty-state">No file uploaded</span>
							</td>
							<td>
								<div class="row-actions">
									<input
										:ref="element => setFileInput(element, list.id)"
										class="file-input"
										type="file"
										accept=".csv,text/csv"
										@change="uploadFile($event, list)"
									/>
									<button type="button" class="action-button upload-button" @click="openFilePicker(list.id)">
										<font-awesome-icon icon="fa-upload" />
										Upload
									</button>
									<button
										type="button"
										class="action-button download-button"
										:disabled="!list.apiManaged && !list.fileContent"
										@click="downloadFile(list)"
									>
										<font-awesome-icon icon="fa-download" />
										Download
									</button>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<section class="add-contractor-section">
				<div class="section-header">
					<h3>Upload a new contractor list</h3>
				</div>

				<div class="add-contractor-form">
					<label class="form-field">
						<span>Area <span class="required">*</span></span>
						<input v-model="newContractor.area" type="text" placeholder="Enter area" required />
					</label>

					<label class="form-field">
						<span>Equipment <span class="required">*</span></span>
						<select v-model="newContractor.equipment" required>
							<option disabled value="">Select equipment</option>
							<option v-for="equipment in availableEquipmentTypes" :key="equipment" :value="equipment">
								{{ equipment }}
							</option>
						</select>
					</label>

					<label class="form-field file-upload-field">
						<span>CSV Upload <span class="required">*</span></span>
						<div class="file-picker">
							<label class="file-picker-button">
								Choose File
								<input
									type="file"
									accept=".csv,text/csv"
									@change="handleNewContractorFile($event)"
									class="file-input-hidden"
									required
								/>
							</label>
							<span class="file-picker-text">{{ newContractorDisplayFileName || 'No file chosen' }}</span>
						</div>
					</label>
					<button type="button" class="action-button upload-button" @click="addContractor">
						<font-awesome-icon icon="fa-upload" />
						Upload
					</button>
				</div>

				<div class="form-actions">
					<p v-if="newContractorError" class="inline-error" role="alert">{{ newContractorError }}</p>
					<p v-if="newContractorRenamedFileName" class="file-name-note">
						NOTE: Your file will be automatically renamed to {{ newContractorRenamedFileName }} based on the area and equipment type selected.
					</p>
				</div>
			</section>
	</div>
</template>

<script>
const fleetCustomerGuid = 'AD19537023AE4333B185521BF7078F60'

export default {
	name: 'FleetUpdateContractorsList',
	data() {
		return {
			customerGUID: '',
			uploadError: '',
			listError: '',
			isLoadingLists: false,
			newContractorError: '',
			popup: {
				show: false,
				messageBefore: '',
				fileName: '',
				messageAfter: '',
				type: 'success'
			},
			newContractor: {
				area: '',
				equipment: '',
				file: null,
				fileName: ''
			},
			availableEquipmentTypes: [
				'Trucks',
				'Machines'
			],
			fileInputs: {},
			contractorLists: []
		}
	},
	computed: {
		newContractorDisplayFileName() {
			return this.newContractor.fileName
		},
		newContractorRenamedFileName() {
			const { area, equipment } = this.newContractor
			return area.trim() && equipment
				? this.createContractorFileName(area.trim(), equipment)
				: ''
		}
	},
	mounted() {
		this.customerGUID = (localStorage.getItem('customerGUID') || fleetCustomerGuid).toUpperCase()
		this.fetchTargetLists()
	},
	methods: {
		showPopup(message, type = 'success', fileName = '') {
			const fileNameIndex = fileName ? message.indexOf(fileName) : -1
			this.popup = {
				show: true,
				messageBefore: fileNameIndex >= 0 ? message.slice(0, fileNameIndex) : message,
				fileName: fileNameIndex >= 0 ? fileName : '',
				messageAfter: fileNameIndex >= 0 ? message.slice(fileNameIndex + fileName.length) : '',
				type
			}
		},
		hidePopup() {
			this.popup = {
				show: false,
				messageBefore: '',
				fileName: '',
				messageAfter: '',
				type: 'success'
			}
		},
		normalizeFileNamePart(value) {
			return value
				.normalize('NFKD')
				.replace(/[\u0300-\u036f]/g, '')
				.replace(/[^a-zA-Z0-9]+/g, ' ')
				.trim()
				.split(/\s+/)
				.map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
				.join('_')
		},
		createContractorFileName(area, equipment) {
			const areaPart = this.normalizeFileNamePart(area)
			const equipmentPart = this.normalizeFileNamePart(equipment)
			return `${areaPart}_${equipmentPart}.csv`
		},
		createContractorId(area, equipment) {
			return this.createContractorFileName(area, equipment).replace(/\.csv$/i, '')
		},
		async fetchTargetLists() {
			this.isLoadingLists = true
			this.listError = ''

			try {
				const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS
				const url = `${baseUrl}/customers/${this.customerGUID}/campaign/fph-availability/target-lists`
				const response = await fetch(url, {
					method: 'GET',
					headers: { Accept: 'application/json' }
				})

				if (!response.ok) {
					const errorMessage = await response.text().catch(() => '')
					throw new Error(errorMessage || `Unable to load contractor lists (${response.status}).`)
				}

				const result = await response.json()
				if (
					result.success !== true ||
					!Array.isArray(result.files) ||
					result.files.some(file => !file || typeof file.file !== 'string')
				) {
					throw new Error('The contractor list response was not in the expected format.')
				}

				console.info('[Contractor lists] GET succeeded', {
					url,
					status: response.status,
					fileCount: result.files.length,
					files: result.files
				})
				this.contractorLists = result.files.map(file => {
					const fileName = file.file
					const baseName = fileName.replace(/\.csv$/i, '')
					return {
						id: fileName,
						name: baseName.replace(/_/g, ' '),
						area: file.area || '',
						equipment: file.equipment_type || '',
						fileName,
						remoteUploaded: true,
						apiManaged: true
					}
				})
			} catch (error) {
				this.listError = error.message || 'Unable to load contractor lists.'
				console.error('[Contractor lists] GET request failed', error)
			} finally {
				this.isLoadingLists = false
			}
		},
		handleNewContractorFile(event) {
			const file = event.target.files?.[0]
			event.target.value = ''
			if (!file) {
				this.newContractor.file = null
				this.newContractor.fileName = ''
				return
			}

			const validationError = this.validateContractorFile(file)
			if (validationError) {
				this.setNewContractorError(validationError)
				return
			}

			this.newContractorError = ''
			this.newContractor.file = file
			this.newContractor.fileName = file.name
		},
		validateContractorFile(file) {
			if (!/\.csv$/i.test(file.name)) {
				return 'Choose a CSV file. Only .csv files are supported.'
			}
			if (file.type && !['text/csv', 'text/plain', 'application/vnd.ms-excel', 'application/csv'].includes(file.type)) {
				return 'The selected file is not a supported CSV file.'
			}
			if (file.size === 0) {
				return 'The selected CSV file is empty.'
			}
			return ''
		},
		setNewContractorError(message) {
			this.newContractorError = message
			this.newContractor.file = null
			this.newContractor.fileName = ''
		},
		async uploadNewContractorFile(file, contractor) {
			const formData = new FormData()
			formData.append('file', file, contractor.fileName)
			formData.append('fileName', contractor.fileName)
			const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS
			const url = `${baseUrl}/customers/${this.customerGUID}/campaign/fph-availability/target-lists/upload`
			console.log('Uploading custom contractor CSV', {
				customerGUID: this.customerGUID,
				url,
				fileName: contractor.fileName || file.name,
				fileSize: file.size,
				fileType: file.type
			})

			const response = await fetch(url, {
				method: 'POST',
				body: formData,
				headers: { Accept: 'application/json' }
			})

			if (!response.ok) {
				const errorMessage = await response.text().catch(() => '')
				console.log('Custom contractor upload failed response', {
					status: response.status,
					statusText: response.statusText,
					errorMessage
				})
				throw new Error(errorMessage || `Upload failed (${response.status}).`)
			}

			console.log('Custom contractor upload success response', {
				status: response.status,
				statusText: response.statusText,
				responseText: await response.text().catch(() => '')
			})

			contractor.remoteUploaded = true
		},
		async addContractor() {
			const area = this.newContractor.area.trim()
			const equipment = this.newContractor.equipment

			this.newContractorError = ''

			if (!area || !equipment || !this.newContractor.file) {
				this.setNewContractorError('Area, equipment and contractor list CSV file are required')
				return
			}
			if ([area, equipment].some(value => !this.normalizeFileNamePart(value))) {
				this.setNewContractorError('Area and Equipment must contain letters or numbers.')
				return
			}
			if (!this.availableEquipmentTypes.includes(equipment)) {
				this.setNewContractorError('Select a valid equipment option.')
				return
			}
			const fileValidationError = this.validateContractorFile(this.newContractor.file)
			if (fileValidationError) {
				this.setNewContractorError(fileValidationError)
				return
			}

			const fileName = this.createContractorFileName(area, equipment)
			const duplicate = this.contractorLists.some(list => {
				return (list.fileName || '').toLowerCase() === fileName.toLowerCase() ||
					((list.area || '').trim().toLowerCase() === area.toLowerCase() &&
						(list.equipment || '').trim().toLowerCase() === equipment.toLowerCase())
			})
			if (duplicate) {
				this.setNewContractorError('A contractor list with this Area and Equipment already exists.')
				return
			}

			const contractor = {
				id: this.createContractorId(area, equipment),
				name: `${area} ${equipment}`,
				area,
				equipment,
				fileName,
				remoteUploaded: false,
				apiManaged: true
			}

			try {
				const sourceFile = this.newContractor.file
				const renamedFile = new File([sourceFile], fileName, {
					type: sourceFile.type || 'text/csv',
					lastModified: sourceFile.lastModified
				})
				await this.uploadNewContractorFile(renamedFile, contractor)
				await this.fetchTargetLists()
				this.newContractor = { area: '', equipment: '', file: null, fileName: '' }
				this.showPopup(`File renamed to ${contractor.fileName} and uploaded successfully.`, 'success', contractor.fileName)
			} catch (error) {
				this.newContractorError = error.message || 'Unable to upload the contractor file.'
			}
		},
		setFileInput(element, listId) {
			if (element) this.fileInputs[listId] = element
		},
		openFilePicker(listId) {
			this.fileInputs[listId]?.click()
		},
		async uploadFile(event, list) {
			const file = event.target.files?.[0]
			event.target.value = ''
			console.info('[Contractor upload] File input changed', {
				listId: list.id,
				apiManaged: list.apiManaged,
				fileSelected: Boolean(file),
				fileName: file?.name || ''
			})
			if (!file) {
				console.warn('[Contractor upload] No file selected; POST was not sent.')
				return
			}
			this.uploadError = ''
			if (!file.name.toLowerCase().endsWith('.csv')) {
				console.warn('[Contractor upload] Invalid file type; POST was not sent.', { fileName: file.name })
				this.uploadError = 'Please choose a CSV file.'
				this.showPopup(this.uploadError, 'error', list.fileName)
				return
			}
			if (list.apiManaged) {
				await this.uploadTargetListFile(file, list)
			}
		},
		async uploadTargetListFile(file, list) {
			try {
				const formData = new FormData()
				formData.append('file', file, file.name)
				formData.append('fileName', list.fileName || file.name)
				const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS
				const url = `${baseUrl}/customers/${this.customerGUID}/campaign/fph-availability/target-lists/upload`
				console.log('Uploading contractor CSV', {
					customerGUID: this.customerGUID,
					url,
					fileName: list.fileName || file.name,
					fileSize: file.size,
					fileType: file.type
				})
				const startedAt = performance.now()
				const response = await fetch(url, {
					method: 'POST',
					body: formData,
					headers: { Accept: 'application/json' }
				})
				const responseText = await response.text()
				const uploadResult = {
					url,
					method: 'POST',
					status: response.status,
					statusText: response.statusText,
					ok: response.ok,
					durationMs: Math.round(performance.now() - startedAt),
					responseBody: responseText
				}

				if (!response.ok) {
					console.error('[Contractor upload] POST failed', uploadResult)
					throw new Error(responseText || `Upload failed (${response.status}).`)
				}

				console.info('[Contractor upload] POST succeeded', uploadResult)
				list.remoteUploaded = true
				this.showPopup(`${list.fileName} uploaded successfully.`, 'success', list.fileName)
				await this.fetchTargetLists()
			} catch (error) {
				console.error('[Contractor upload] POST request error', {
					url: `${process.env.VUE_APP_VORTEX_PORTAL_WS}/customers/${this.customerGUID}/campaign/fph-availability/target-lists/upload`,
					error
				})
				this.uploadError = error.message || `Unable to upload ${list.fileName}.`
				this.showPopup(this.uploadError, 'error', list.fileName)
			}
		},
		async downloadFile(list) {
			if (list.apiManaged) {
				try {
					const baseUrl = process.env.VUE_APP_VORTEX_PORTAL_WS
					const fileName = encodeURIComponent(list.fileName)
					const url = `${baseUrl}/customers/${this.customerGUID}/campaign/fph-availability/target-lists/${fileName}/download`
					const response = await fetch(url, { headers: { Accept: 'text/csv' } })

					if (!response.ok) {
						const errorMessage = await response.text().catch(() => '')
						throw new Error(errorMessage || `Download failed (${response.status}).`)
					}

					const blob = await response.blob()
					const downloadUrl = URL.createObjectURL(blob)
					const link = document.createElement('a')
					link.href = downloadUrl
					link.download = list.fileName
					link.click()
					URL.revokeObjectURL(downloadUrl)
					this.uploadError = ''
				} catch (error) {
					this.uploadError = error.message || `Unable to download ${list.fileName}.`
				}
				return
			}

			if (!list.fileContent) return

			const blob = new Blob([list.fileContent], { type: 'text/csv;charset=utf-8' })
			const downloadUrl = URL.createObjectURL(blob)
			const link = document.createElement('a')
			link.href = downloadUrl
			link.download = list.fileName
			link.click()
			URL.revokeObjectURL(downloadUrl)
		}
	}
}
</script>

<style scoped>
.contractor-lists-content {
	width: 100%;
}

.table-scroll {
	overflow-x: auto;
	border: 1px solid #dfe5e8;
	border-radius: 6px;
	background: #fff;
}

.contractor-table {
	width: 100%;
	border-collapse: collapse;
	text-align: left;
}

.contractor-table th,
.contractor-table td {
	padding: 16px 20px;
	border-bottom: 1px solid #e7ecee;
}

.contractor-table th {
	background: #f7f9fa;
	color: #596970;
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
}

.contractor-table tbody tr:last-child td {
	border-bottom: 0;
}

.list-name {
	color: #24343b;
	font-size: 15px;
	font-weight: 600;
}

.empty-state {
	margin-top: 4px;
	color: #718087;
	font-size: 13px;
}

.file-name {
	color: #34464e;
	font-size: 14px;
	overflow-wrap: anywhere;
}

.actions-heading {
	width: 260px;
}

.row-actions {
	display: flex;
	gap: 8px;
	white-space: nowrap;
}

.file-input {
	display: none;
}

.action-button {
	min-height: 36px;
	padding: 0 12px;
	border: 1px solid #d7dfe2;
	border-radius: 4px;
	background: #fff;
	color: #34464e;
	cursor: pointer;
	font: inherit;
	font-size: 13px;
}

.action-button svg {
	margin-right: 6px;
}

.upload-button {
	border-color: #e45f2b;
	background: #e45f2b;
	color: #fff;
}

.action-button:hover:not(:disabled) {
	filter: brightness(0.94);
}

.action-button:disabled {
	color: #99a4a9;
	cursor: not-allowed;
}

.popup-overlay {
	position: fixed;
	inset: 0;
	z-index: 2000;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
	background: rgba(65, 70, 74, 0.35);
}

.popup-box {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 12px;
	width: min(440px, 100%);
	padding: 16px 18px 14px;
	border: 1px solid #dfe5e8;
	border-radius: 8px;
	background: rgba(255, 255, 255, 0.96);
	box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
	text-align: center;
}

.popup-box.success {
	border-color: #1b7f4d;
}

.popup-box.error {
	border-color: #a52822;
}

.popup-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 50%;
	font-size: 24px;
	font-weight: 700;
	color: #fff;
	line-height: 1;
}

.popup-box.success .popup-icon {
	background: #1b7f4d;
}

.popup-box.error .popup-icon {
	background: #a52822;
}

.popup-message {
	margin: 0;
	color: #111827;
	font-size: 18px;
	font-weight: 400;
	line-height: 1.4;
	letter-spacing: -0.02em;
}

.popup-message strong {
	font-weight: 400;
}

.popup-button {
	width: 100%;
	max-width: 220px;
	height: 42px;
	border: 0;
	border-radius: 8px;
	background: #e45f2b;
	color: #fff;
	font: inherit;
	font-size: 18px;
	font-weight: 800;
	cursor: pointer;
}

.upload-error,
.inline-error {
	display: block;
	margin: 0 0 16px;
	padding: 10px 12px;
	border: 1px solid transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.4;
}

.upload-error {
	border-color: rgba(165, 40, 34, 0.2);
	background: rgba(165, 40, 34, 0.08);
	color: #a52822;
}

.inline-error {
	padding: 0;
	border: 0;
	border-radius: 0;
	background: none;
	outline: none;
	color: #a52822;
}

.file-name-note {
	margin: 0;
	color: #4d5c64;
	font-size: 12px;
	font-weight: 700;
	line-height: 1.4;
}

.add-contractor-section {
	margin-top: 24px;
	padding: 20px;
	border: 1px solid #dfe5e8;
	border-radius: 8px;
	background: #fff;
	font-family: 'Montserrat', sans-serif;
}

.section-header h3 {
	margin: 0 0 16px;
	color: #24343b;
	font-size: 1.1rem;
	font-weight: 500;
	text-transform: uppercase;
}

.add-contractor-form {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
	gap: 1.5rem;
	align-items: end;
}

.form-field {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	color: #374151;
	font-size: 0.8rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	text-transform: uppercase;
	min-width: 0;
}

.required {
	color: #e45f2b;
}

.form-field input,
.form-field select {
	width: 100%;
	height: 38px;
	padding: 0.5rem 0.75rem;
	border: 1px solid #ccc;
	border-radius: 4px;
	background: #fff;
	color: #24343b;
	font: inherit;
	font-size: 0.875rem;
	text-transform: none;
	box-sizing: border-box;
}

.form-field select {
	cursor: pointer;
}

.file-picker {
	display: flex;
	align-items: center;
	gap: 0;
	height: 38px;
	border: 1px solid #ccc;
	border-radius: 4px;
	background: #fff;
	box-sizing: border-box;
}

.file-picker-button {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 112px;
	height: 36px;
	padding: 0 12px;
	border-right: 1px solid #ccc;
	background: #fff;
	color: #34464e;
	font-size: 0.875rem;
	font-weight: 500;
	cursor: pointer;
	box-sizing: border-box;
}

.file-input-hidden {
	display: none;
}

.file-picker-text {
	flex: 1;
	min-width: 0;
	padding: 0 10px;
	color: #4d5c64;
	font-size: 0.8rem;
	font-weight: 500;
	text-transform: none;
	overflow-wrap: anywhere;
}

.form-field input:focus,
.form-field select:focus {
	outline: none;
	border-color: #e45f2b;
	box-shadow: 0 0 0 3px rgba(228, 95, 43, 0.12);
}

.form-field input:hover,
.form-field select:hover {
	border-color: #e45f2b;
}

.add-contractor-form > .upload-button {
	width: auto;
	min-height: 36px;
	height: 36px;
	padding: 0 12px;
	border-radius: 4px;
	font-family: 'Roboto', sans-serif;
	font-size: 13px;
	font-weight: 400;
	white-space: nowrap;
}

.form-actions {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	justify-content: flex-start;
	gap: 12px;
	margin-top: 12px;
}

.primary-button {
	min-height: 42px;
	padding: 0 18px;
	border: 0;
	border-radius: 6px;
	background: #e45f2b;
	color: #fff;
	cursor: pointer;
	font: inherit;
	font-size: 16px;
	font-weight: 700;
}

@media (max-width: 700px) {
	.add-contractor-form {
		grid-template-columns: 1fr;
	}

	.form-actions {
		flex-direction: column;
		align-items: flex-start;
	}
	.contractor-table th,
	.contractor-table td {
		padding: 12px;
	}

	.actions-heading {
		width: auto;
	}

	.row-actions {
		flex-direction: column;
	}
}

@media (min-width: 701px) and (max-width: 960px) {
	.add-contractor-form {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}
</style>
